import React from 'react';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

interface PromptInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  helperText?: string;
  required?: boolean;
  rows?: number;
  maxLength?: number;
  disabled?: boolean;
}

/**
 * PromptInput - Textarea component for AI prompts with character counter
 *
 * Used for both image and video generation prompts.
 * Includes character counting and validation.
 *
 * Following Pumpd's form input patterns with space-y-2 spacing
 */
export const PromptInput: React.FC<PromptInputProps> = ({
  label,
  value,
  onChange,
  placeholder,
  helperText,
  required = false,
  rows = 6,
  maxLength = 500,
  disabled = false
}) => {
  const charactersUsed = value.length;
  const isNearLimit = charactersUsed > maxLength * 0.8;
  const isAtLimit = charactersUsed >= maxLength;

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <Label htmlFor="prompt-input">
          {label} {required && <span className="text-destructive">*</span>}
        </Label>
        <span
          className={`text-xs ${
            isAtLimit
              ? 'text-destructive font-medium'
              : isNearLimit
              ? 'text-orange-500'
              : 'text-muted-foreground'
          }`}
        >
          {charactersUsed} / {maxLength}
        </span>
      </div>

      <Textarea
        id="prompt-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        maxLength={maxLength}
        disabled={disabled}
        className="resize-none"
      />

      {helperText && (
        <p className="text-xs text-muted-foreground">{helperText}</p>
      )}
    </div>
  );
};

export default PromptInput;
